/**
 * Skeleton.charts — Presets de carga para tarjetas KPI y gráficos.
 *
 * QUÉ HACE: skeletons de tarjeta KPI, gráfico de barras y donut, construidos
 *   sobre la primitiva `Skeleton`.
 * QUÉ INVOCA: Skeleton (Skeleton.base).
 * QUÉ EXPORTA: SkeletonMetricCard, SkeletonChart, SkeletonDonut.
 * QUÉ NO DEBE HACER: no contiene lógica de negocio ni datos reales.
 */

import React from "react";
import { Skeleton } from "./Skeleton.base";

/* ═══════════════════════════════════════════════════════════════
   PRESET: METRIC CARD (dashboard KPI card)
   ═══════════════════════════════════════════════════════════════ */

/**
 * Coincide con el layout de MetricCard (dashboard) y MetricStatsCards (metrics).
 * Muestra: icono cuadrado + label + valor grande + subtítulo opcional.
 */
export const SkeletonMetricCard: React.FC<{ className?: string }> = ({
  className = "",
}) => (
  <div
    aria-hidden="true"
    className={`bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700
      rounded-xl p-4 shadow-sm flex flex-col gap-3 ${className}`}
  >
    {/* Ícono + badge (esquina superior derecha) */}
    <div className="flex items-start justify-between">
      <Skeleton width="w-9" height="h-9" rounded="lg" />
      <Skeleton width="w-14" height="h-5" rounded="full" />
    </div>
    {/* Label */}
    <Skeleton width="w-24" height="h-3" rounded="md" />
    {/* Valor grande */}
    <Skeleton width="w-32" height="h-7" rounded="md" />
    {/* Subtítulo */}
    <Skeleton width="w-20" height="h-2.5" rounded="md" />
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   PRESET: CHART (InteractiveBarChart / SalesAreaChart)
   ═══════════════════════════════════════════════════════════════ */

interface SkeletonChartProps {
  /** Cantidad de barras a mostrar (por defecto 7) */
  bars?: number;
  /** Alto del área del gráfico en px (por defecto 200) */
  chartHeight?: number;
  className?: string;
  /** Llena el alto de su contenedor (tarjetas de cuadrícula redimensionable). */
  fillHeight?: boolean;
}

/**
 * Esqueleto completo de un gráfico: cabecera en 3 filas (título + controles,
 * subtítulo y badge) y área de barras de alturas variables. Coincide con el
 * layout real de `ChartCard` para que no haya salto visual al terminar la carga.
 */
export const SkeletonChart: React.FC<SkeletonChartProps> = ({
  bars = 7,
  chartHeight = 200,
  className = "",
  fillHeight = false,
}) => {
  /* Alturas de barras siempre iguales para un layout predecible */
  const barHeights = [55, 80, 40, 90, 65, 75, 50, 85, 60, 70, 45, 88];

  return (
    <div
      aria-hidden="true"
      className={`bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700
        rounded-xl shadow-sm overflow-hidden ${fillHeight ? "flex flex-col h-full" : ""} ${className}`}
    >
      {/* Header en 3 filas (igual que ChartCard) */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Skeleton width="w-5" height="h-5" rounded="md" />
            <Skeleton width="w-36" height="h-4" rounded="md" />
          </div>
          {/* Selector de vista + botón menú ⋮ */}
          <div className="flex shrink-0 items-center gap-0.5">
            <Skeleton width="w-8" height="h-7" rounded="lg" />
            <Skeleton width="w-8" height="h-7" rounded="lg" />
          </div>
        </div>
        {/* Subtítulo */}
        <Skeleton width="w-44" height="h-2.5" rounded="md" className="mt-1.5" />
        {/* Badge de dato clave */}
        <Skeleton width="w-28" height="h-4" rounded="full" className="mt-1.5" />
      </div>

      {/* Área del gráfico */}
      <div
        className={`px-5 py-4 ${fillHeight ? "flex-1 min-h-0" : ""}`}
        style={fillHeight ? undefined : { height: chartHeight + 48 }}
      >
        {/* Líneas de eje Y */}
        <div className="relative h-full flex flex-col justify-between pb-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <Skeleton width="w-8" height="h-2.5" rounded="md" />
              <div className="flex-1 h-px skeleton opacity-50" />
            </div>
          ))}

          {/* Barras */}
          <div
            className="absolute bottom-6 left-14 right-0 flex items-end justify-around gap-1"
            style={fillHeight ? { top: 0 } : { height: chartHeight }}
          >
            {Array.from({ length: bars }).map((_, i) => {
              const heightPct = barHeights[i % barHeights.length];
              return (
                <div
                  key={i}
                  className="skeleton flex-1 rounded-t-sm"
                  style={{ height: `${heightPct}%` }}
                />
              );
            })}
          </div>

          {/* Etiquetas eje X */}
          <div className="flex justify-around gap-1 mt-1">
            {Array.from({ length: bars }).map((_, i) => (
              <Skeleton
                key={i}
                width="w-6"
                height="h-2"
                rounded="md"
                className="flex-1"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   PRESET: DONUT CHART (CategoryDonutChart)
   ═══════════════════════════════════════════════════════════════ */

interface SkeletonDonutProps {
  /** Número de filas de leyenda (por defecto 5) */
  legendRows?: number;
  className?: string;
}

/**
 * Esqueleto de un gráfico donut: navegación temporal (pills) + círculo donut
 * + leyenda con filas de color-nombre-porcentaje.
 * Coincide con CategoryDonutChart.
 */
export const SkeletonDonut: React.FC<SkeletonDonutProps> = ({
  legendRows = 5,
  className = "",
}) => (
  <div
    aria-hidden="true"
    className={`bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700
      rounded-xl shadow-sm p-4 flex flex-col gap-4 ${className}`}
  >
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Skeleton width="w-5" height="h-5" rounded="md" />
        <Skeleton width="w-40" height="h-4" rounded="md" />
      </div>
      <Skeleton width="w-7" height="h-7" rounded="lg" />
    </div>

    {/* Pills de período */}
    <div className="flex gap-1.5 flex-wrap">
      {[36, 52, 44, 48].map((w, i) => (
        <div
          key={i}
          className="skeleton rounded-full h-6"
          style={{ width: w }}
        />
      ))}
    </div>

    {/* Círculo donut */}
    <div className="flex items-center justify-center py-2">
      <div className="relative">
        <Skeleton width="w-44" height="h-44" rounded="full" />
        {/* Hueco central del donut */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-white dark:bg-slate-800" />
        </div>
      </div>
    </div>

    {/* Leyenda */}
    <div className="space-y-2.5">
      {Array.from({ length: legendRows }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <Skeleton width="w-3" height="h-3" rounded="full" />
          <Skeleton width="w-24" height="h-3" rounded="md" className="flex-1" />
          <Skeleton width="w-10" height="h-3" rounded="md" />
          <Skeleton width="w-8" height="h-3" rounded="md" />
        </div>
      ))}
    </div>
  </div>
);
